/**
 * A help article's markdown as React elements (no dangerouslySetInnerHTML).
 * Content comes from curated priv/help/ files, not user input, and the
 * server has already rendered it for this realm.
 *
 * Headings, lists, paragraphs, **bold**, `code`, and links:
 *
 * - `[text](claro:<screen>)` (CLA-1828) goes to that screen in the app. The
 *   article's `links` map (from `GET /help/articles/:slug`) says where each
 *   screen is on the web; `onNavigate` closes whatever is open and goes there.
 *   A screen missing from the map (no web page, or not one this user can
 *   open) is plain text. With no map at all (the Help library's preview) the
 *   link is shown, dotted, but goes nowhere.
 * - `[text](https://…)` opens in a new tab.
 * - Any other link is its text.
 */
import type { AssistTarget } from '@/api/types'

export type HelpLinks = Record<string, AssistTarget>

interface LinkProps {
  links?: HelpLinks
  onNavigate?: (webPath: string) => void
}

export function MarkdownBody({ content, links, onNavigate }: { content: string } & LinkProps) {
  const blocks = content.split('\n\n').filter((b) => b.trim())
  const inline = (text: string) => <InlineText text={text} links={links} onNavigate={onNavigate} />

  return (
    <div className="space-y-3">
      {blocks.map((block, i) => {
        block = block.trim()
        if (!block) return null

        // The header already shows the title, so an article's own "# Title"
        // line is not repeated (it used to print as "# Title").
        if (block.startsWith('# ')) {
          block = block.split('\n').slice(1).join('\n').trim()
          if (!block) return null
        }

        if (block.startsWith('### '))
          return <h3 key={i} className="text-sm font-semibold text-ink mt-4 mb-1">{block.slice(4)}</h3>

        if (block.startsWith('## '))
          return <h2 key={i} className="text-base font-semibold text-ink mt-6 mb-2">{block.slice(3)}</h2>

        if (block.match(/^[-*] /m)) {
          const items = block.split('\n').filter((l) => l.match(/^[-*] /))
          return (
            <ul key={i} className="list-disc pl-5 space-y-1">
              {items.map((item, j) => (
                <li key={j} className="text-sm text-ink-secondary leading-relaxed">
                  {inline(item.replace(/^[-*] /, ''))}
                </li>
              ))}
            </ul>
          )
        }

        if (block.match(/^\d+\. /m)) {
          const items = block.split('\n').filter((l) => l.match(/^\d+\. /))
          return (
            <ol key={i} className="list-decimal pl-5 space-y-1">
              {items.map((item, j) => (
                <li key={j} className="text-sm text-ink-secondary leading-relaxed">
                  {inline(item.replace(/^\d+\. /, ''))}
                </li>
              ))}
            </ol>
          )
        }

        return (
          <p key={i} className="text-sm text-ink-secondary leading-relaxed">
            {inline(block.replace(/\n/g, ' '))}
          </p>
        )
      })}
    </div>
  )
}

const LINK = /^\[([^\]\n]*)\]\(([^)\s]+)\)$/

/** Renders inline markdown (bold, code, links) as React elements. */
function InlineText({ text, links, onNavigate }: { text: string } & LinkProps) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]\n]*\]\([^)\s]+\))/)

  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**'))
          return <strong key={i} className="font-medium text-ink">{part.slice(2, -2)}</strong>

        if (part.startsWith('`') && part.endsWith('`'))
          return <code key={i} className="text-xs bg-surface-secondary px-1 py-0.5 rounded">{part.slice(1, -1)}</code>

        const link = part.match(LINK)
        if (link) return <Link key={i} label={link[1]} href={link[2]} links={links} onNavigate={onNavigate} />

        return <span key={i}>{part}</span>
      })}
    </>
  )
}

function Link({ label, href, links, onNavigate }: { label: string; href: string } & LinkProps) {
  if (href.startsWith('claro:')) {
    const screen = href.slice('claro:'.length)
    if (!links) {
      return (
        <span className="text-claro-700 underline decoration-dotted underline-offset-2" title={`Opens ${screen} in the app`}>
          {label}
        </span>
      )
    }
    const webPath = links[screen]?.web_path
    if (!webPath || !onNavigate) return <span>{label}</span>
    return (
      <a
        href={webPath}
        onClick={(e) => {
          // A new tab or window (⌘/Ctrl/Shift-click) is the browser's to open.
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return
          e.preventDefault()
          onNavigate(webPath)
        }}
        className="font-medium text-claro-700 underline underline-offset-2 hover:text-claro-800"
      >
        {label}
      </a>
    )
  }

  if (href.startsWith('https://')) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium link"
      >
        {label}
      </a>
    )
  }

  return <span>{label}</span>
}
